/* ============================================================
   ilo sitelen — the sentence machine
   ------------------------------------------------------------
   A full-screen, self-contained sub-site with two modes:
     - "o sitelen" (draw!): type toki pona, watch the machine draw it.
     - "o toki" (say it!): describe a random scene, the machine grades it.

   All parsing / grading / generation lives in src/lib/iloSitelen.js and
   src/lib/tokiPonaCompose.js — this file is purely presentational glue.
   ============================================================ */

import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  renderScene,
  matchDescription,
  randomScene,
  specToSentence,
  alternatePhrasings,
  SUPPORTED_WORDS,
  ENTITIES,
  PRONOUN_ENTITIES,
  ACTIONS,
  COLOR_MODS,
  SIZE_MODS,
  COUNT_MODS,
  MOOD_MODS,
  PLACES,
} from "../lib/iloSitelen";
import { completions } from "../lib/tokiPonaCompose";
import SceneRenderer, { GlyphIcon } from "../components/tokipona/SceneRenderer";
import { WORDS } from "../lib/tokiPonaData";
import "../styles/iloSitelen.css";

const STORAGE_KEY = "ilo-sitelen-v1";

const EXAMPLES = [
  "soweli loje li moku e kili",
  "waso lili mute li tawa tomo",
  "mi lape lon kasi a",
];

const LEVELS = [
  { n: 1, label: "nimi" },
  { n: 2, label: "pali" },
  { n: 3, label: "ijo" },
  { n: 4, label: "ma" },
];

// Picture-glossary groupings, sliced out of ENTITIES so word order always
// tracks the source list in src/lib/iloSitelen.js.
const CREATURE_WORDS = ENTITIES.slice(0, ENTITIES.indexOf("kili"));
const NATURE_WORDS = ENTITIES.slice(ENTITIES.indexOf("kili"), ENTITIES.indexOf("tomo"));
const THING_WORDS = ENTITIES.slice(ENTITIES.indexOf("tomo"));

/* ---------------- small helpers ---------------- */

const lastWordOf = (text) => {
  const parts = String(text || "").split(/\s+/);
  return parts[parts.length - 1] || "";
};

// Replace the (partial) last word of `text` with `word`, keeping a
// trailing space so typing can continue straight into the next word.
const replaceLastWord = (text, word) => {
  const trimmed = String(text || "").replace(/\s+$/, "");
  const parts = trimmed.length ? trimmed.split(/\s+/) : [];
  if (parts.length) parts[parts.length - 1] = word;
  else parts.push(word);
  return `${parts.join(" ")} `;
};

// Friendly, in-character explanations for the reason codes the lib emits
// when a reading can't be turned into a drawable scene.
function reasonHint(reason) {
  const { code, word } = reason || {};
  switch (code) {
    case "entity":
      return `the machine can't draw «${word}»`;
    case "verb":
      return `the machine doesn't know how to act out «${word}»`;
    case "preverb":
      return "preverbs are beyond the machine's little brain";
    case "pi":
      return "pi groups make its gears jam";
    case "place":
      return `«${word}» isn't a place it can paint`;
    case "manner":
    case "mod":
      return `it can't draw «${word}» that way`;
    case "multi-subject":
    case "multi-pred":
      return "one thing at a time, please";
    default:
      return word
        ? `the machine trips over «${word}»`
        : "the machine just stares blankly at that";
  }
}

// First segment of a word's gloss (WORDS[word].g), trimmed — "land mammal"
// out of "land mammal", or "red" out of "red". Falls back to "" if the
// word has no entry (shouldn't happen for anything on this page).
function shortGloss(word) {
  const entry = WORDS[word];
  if (!entry || !entry.g) return "";
  return entry.g.split("/")[0].trim();
}

function loadStreaks() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

/* ---------------- shared bits ---------------- */

function WordInput({ value, onChange, onEnter, placeholder }) {
  const lastWord = lastWordOf(value);
  const chips = completions(lastWord, SUPPORTED_WORDS);
  return (
    <div className="ilos-input-wrap">
      <input
        className="ilos-input"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && onEnter) {
            e.preventDefault();
            onEnter();
          }
        }}
        placeholder={placeholder}
        autoComplete="off"
        autoCapitalize="off"
        spellCheck="false"
      />
      {chips.length > 0 && (
        <div className="ilos-chips">
          {chips.map((w) => (
            <button
              key={w}
              type="button"
              className="ilos-chip"
              onClick={() => onChange(replaceLastWord(value, w))}
            >
              {w}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function SputterPanel({ reasons, errors }) {
  if (!reasons?.length && !errors?.length) return null;
  return (
    <div className="ilos-sputter">
      {reasons?.length > 0 && (
        <>
          <p className="ilos-sputter-lead">*klunk — wrrrrr* the machine sputters:</p>
          <ul className="ilos-hint-list">
            {reasons.map((r, i) => (
              <li key={i}>{reasonHint(r)}</li>
            ))}
          </ul>
        </>
      )}
      {errors?.length > 0 && (
        <ul className="ilos-hint-list ilos-hint-list-grammar">
          {errors.map((e, i) => (
            <li key={i}>
              {e.message}
              {e.hint && <span className="ilos-hint-extra"> {e.hint}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function VocabGroup({ label, words, dots }) {
  return (
    <div className="ilos-vocab-group">
      <h4>{label}</h4>
      <div className="ilos-vocab-words">
        {words.map((w) => (
          <span key={w} className="ilos-vocab-word">
            {dots && <span className="ilos-dot" style={{ background: dots[w] }} />}
            {w}
          </span>
        ))}
      </div>
    </div>
  );
}

function VocabPanel() {
  return (
    <details className="ilos-vocab">
      <summary>nimi ale — what can it draw?</summary>
      <div className="ilos-vocab-body">
        <VocabGroup label="things" words={[...ENTITIES, ...PRONOUN_ENTITIES]} />
        <VocabGroup label="actions" words={Object.keys(ACTIONS)} />
        <VocabGroup label="colors" words={Object.keys(COLOR_MODS)} dots={COLOR_MODS} />
        <VocabGroup label="sizes" words={Object.keys(SIZE_MODS)} />
        <VocabGroup label="counts" words={Object.keys(COUNT_MODS)} />
        <VocabGroup label="moods" words={MOOD_MODS} />
        <VocabGroup label="places" words={PLACES} />
      </div>
    </details>
  );
}

/* ---------------- o sitelen (draw!) ---------------- */

function DrawMode() {
  const [input, setInput] = useState("");
  const result = useMemo(() => renderScene(input), [input]);

  return (
    <div className="ilos-panel">
      <p className="ilos-prompt">type a toki pona sentence and watch it draw itself.</p>
      <WordInput value={input} onChange={setInput} placeholder="soweli loje li moku e kili" />
      <div className="ilos-examples">
        {EXAMPLES.map((ex) => (
          <button key={ex} type="button" className="ilos-example" onClick={() => setInput(ex)}>
            {ex}
          </button>
        ))}
      </div>

      <div className="ilos-stage">
        {result.scene ? (
          <>
            <div className="ilos-canvas">
              <SceneRenderer scene={result.scene} width={480} />
            </div>
            <p className="ilos-gloss">the machine sees: {result.reading.gloss}</p>
          </>
        ) : input.trim() ? (
          <SputterPanel reasons={result.reasons} errors={result.errors} />
        ) : (
          <p className="ilos-placeholder">…the machine waits, gears idle…</p>
        )}
      </div>

      <VocabPanel />
    </div>
  );
}

/* ---------------- o toki (say it!) ---------------- */

function SayMode() {
  const [level, setLevel] = useState(1);
  const [target, setTarget] = useState(() => randomScene(1, Math.random));
  const [input, setInput] = useState("");
  const [submitResult, setSubmitResult] = useState(null);
  const [revealed, setRevealed] = useState(false);
  const [flash, setFlash] = useState(false);
  const [streaks, setStreaks] = useState(loadStreaks);
  const timeoutRef = useRef(null);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(streaks));
    } catch {
      // localStorage unavailable — streaks just won't persist.
    }
  }, [streaks]);

  useEffect(() => () => timeoutRef.current && clearTimeout(timeoutRef.current), []);

  const newTarget = (lvl) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setTarget(randomScene(lvl, Math.random));
    setInput("");
    setSubmitResult(null);
    setRevealed(false);
    setFlash(false);
  };

  const handleLevel = (n) => {
    setLevel(n);
    newTarget(n);
  };

  const bump = (ok) => {
    setStreaks((prev) => {
      const cur = prev[level] || { current: 0, best: 0 };
      const nextCurrent = ok ? cur.current + 1 : cur.current;
      return { ...prev, [level]: { current: nextCurrent, best: Math.max(cur.best, nextCurrent) } };
    });
  };

  const handleSubmit = () => {
    if (!input.trim() || flash) return;
    const res = matchDescription(input, target);
    setSubmitResult(res);
    if (res.ok) {
      setFlash(true);
      bump(true);
      timeoutRef.current = setTimeout(() => newTarget(level), 1200);
    }
  };

  const handleGiveUp = () => {
    setRevealed(true);
    setSubmitResult(null);
    setStreaks((prev) => ({
      ...prev,
      [level]: { current: 0, best: (prev[level] && prev[level].best) || 0 },
    }));
  };

  const streak = streaks[level] || { current: 0, best: 0 };
  const alts = revealed ? alternatePhrasings(target) : [];

  return (
    <div className="ilos-panel">
      <div className="ilos-levels">
        {LEVELS.map(({ n, label }) => (
          <button
            key={n}
            type="button"
            className={`ilos-level${level === n ? " ilos-level-active" : ""}`}
            onClick={() => handleLevel(n)}
          >
            {label}
          </button>
        ))}
      </div>
      <p className="ilos-streak">
        streak {streak.current} · best {streak.best}
      </p>

      <p className="ilos-prompt">o toki e ni: what do you see?</p>
      <div className="ilos-canvas ilos-canvas-target">
        <SceneRenderer scene={target} width={480} />
      </div>

      {!revealed && !flash && (
        <form
          className="ilos-say-form"
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit();
          }}
        >
          <WordInput value={input} onChange={setInput} onEnter={handleSubmit} placeholder="soweli loje li lape" />
          <div className="ilos-say-actions">
            <button type="submit" className="ilos-btn ilos-btn-primary">
              pana!
            </button>
            <button type="button" className="ilos-btn" onClick={() => newTarget(level)}>
              ante
            </button>
            <button type="button" className="ilos-btn ilos-btn-ghost" onClick={handleGiveUp}>
              o pana e pona
            </button>
          </div>
        </form>
      )}

      {flash && <p className="ilos-success">pona a! ✦</p>}

      {!flash && submitResult && !submitResult.ok && (
        submitResult.scene ? (
          <div className="ilos-compare">
            <div className="ilos-compare-col">
              <p className="ilos-compare-label">you said this ↓</p>
              <div className="ilos-canvas ilos-canvas-small">
                <SceneRenderer scene={submitResult.scene} width={200} />
              </div>
            </div>
            <div className="ilos-compare-col">
              <p className="ilos-compare-label">but the machine wanted ↑</p>
              <div className="ilos-canvas ilos-canvas-small">
                <SceneRenderer scene={target} width={200} />
              </div>
            </div>
          </div>
        ) : (
          <SputterPanel reasons={submitResult.reasons} errors={submitResult.errors} />
        )
      )}

      {revealed && (
        <div className="ilos-reveal">
          <p className="ilos-reveal-lead">the machine would say:</p>
          <p className="ilos-reveal-main">{specToSentence(target)}</p>
          {alts.map((alt) => (
            <p key={alt} className="ilos-reveal-alt">
              {alt}
            </p>
          ))}
          <button type="button" className="ilos-btn ilos-btn-primary" onClick={() => newTarget(level)}>
            new target
          </button>
        </div>
      )}
    </div>
  );
}

/* ---------------- nasin (how?) ---------------- */

function GlyphGridGroup({ label, words, note }) {
  return (
    <div className="ilos-glyph-group">
      <h4>{label}</h4>
      {note && <p className="ilos-glyph-note">{note}</p>}
      <div className="ilos-glyph-grid">
        {words.map((w) => (
          <div key={w} className="ilos-glyph-item">
            <GlyphIcon word={w} size={44} />
            <span className="ilos-glyph-word">{w}</span>
            <span className="ilos-glyph-gloss">{shortGloss(w)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ChipVocabGroup({ label, words, dots, showGloss, note }) {
  return (
    <div className="ilos-vocab-group">
      <h4>{label}</h4>
      {note && <p className="ilos-glyph-note">{note}</p>}
      {words.length > 0 && (
        <div className="ilos-vocab-words">
          {words.map((w) => (
            <span key={w} className="ilos-vocab-word">
              {dots && <span className="ilos-dot" style={{ background: dots[w] }} />}
              {w}
              {showGloss && <span className="ilos-chip-gloss"> {shortGloss(w)}</span>}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function HelpMode() {
  return (
    <div className="ilos-panel ilos-help-panel">
      <section className="ilos-help-section">
        <h3>o sitelen (draw!)</h3>
        <p>
          type a sentence and the machine draws whatever it understands. its eyes only
          recognize the words on this page — everything else it treats as static. when it
          sputters, read its hints closely: they aren't just error messages, they're real
          toki pona grammar, snuck in.
        </p>
      </section>

      <section className="ilos-help-section">
        <h3>o toki (say it!)</h3>
        <p>
          here the machine draws first, and waits for you to describe what it made. it isn't
          picky about phrasing — any grammatically correct description counts, word order and
          all: <span className="ilos-help-code">kili loje</span> and{" "}
          <span className="ilos-help-code">kili li loje</span> are equally welcome. four
          levels: <strong>nimi</strong> (a thing plus a quality), <strong>pali</strong>{" "}
          (someone doing something), <strong>ijo</strong> (doing something to something), and{" "}
          <strong>ma</strong> (all of that, plus a place). stuck? "ante" skips to a new scene
          for free. "o pana e pona" reveals the answer — but it also resets your streak, so the
          machine isn't a total pushover.
        </p>
      </section>

      <div className="ilos-tip">
        <p>
          <strong>tan ni:</strong> this machine is really just a playground for the{" "}
          <a href="/o-kama-sona">o kama sona</a> course — learn words over there, then come
          back here and make them do things.
        </p>
      </div>

      <section className="ilos-help-section ilos-glossary">
        <h3>lipu nimi — picture glossary</h3>
        <GlyphGridGroup
          label="creatures"
          words={CREATURE_WORDS}
          note="mi, sina, and ona all draw as jan (a person) too — just with a little pronoun label next to them."
        />
        <GlyphGridGroup label="nature & food" words={NATURE_WORDS} />
        <GlyphGridGroup label="things" words={THING_WORDS} />
      </section>

      <section className="ilos-help-section">
        <ChipVocabGroup label="actions" words={Object.keys(ACTIONS)} showGloss />
        <ChipVocabGroup label="colors" words={Object.keys(COLOR_MODS)} dots={COLOR_MODS} />
        <ChipVocabGroup label="sizes & counts" words={["suli", "lili", "wan", "tu", "mute"]} />
        <ChipVocabGroup label="moods" words={MOOD_MODS} />
        <ChipVocabGroup label="places" words={[]} note={`after lon: ${PLACES.join(", ")}`} />
      </section>
    </div>
  );
}

/* ---------------- page ---------------- */

export default function IloSitelen() {
  const [mode, setMode] = useState("draw");

  return (
    <div className="ilos-page">
      <header className="ilos-header">
        <h1 className="ilos-title">ilo sitelen</h1>
        <p className="ilos-subtitle">the sentence machine</p>
        <div className="ilos-tabs" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={mode === "draw"}
            className={`ilos-tab${mode === "draw" ? " ilos-tab-active" : ""}`}
            onClick={() => setMode("draw")}
          >
            o sitelen <span className="ilos-tab-sub">draw!</span>
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mode === "say"}
            className={`ilos-tab${mode === "say" ? " ilos-tab-active" : ""}`}
            onClick={() => setMode("say")}
          >
            o toki <span className="ilos-tab-sub">say it!</span>
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mode === "help"}
            className={`ilos-tab${mode === "help" ? " ilos-tab-active" : ""}`}
            onClick={() => setMode("help")}
          >
            nasin <span className="ilos-tab-sub">how?</span>
          </button>
        </div>
      </header>
      <main className="ilos-main">
        {mode === "draw" ? <DrawMode /> : mode === "say" ? <SayMode /> : <HelpMode />}
      </main>
    </div>
  );
}
